import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { listBySection, recentlyVerifiedPages } from "@/lib/playbook-content";

export const Route = createFileRoute("/_authenticated/app/playbook/")({
  component: PlaybookIndex,
});

function PlaybookIndex() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const capabilities = listBySection("capabilities");
  const recent = recentlyVerifiedPages(6);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-8">
      <header className="space-y-3">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">The CairnOS Playbook</h1>
        <p className="text-muted-foreground text-sm max-w-2xl">
          The canonical source of truth for what CairnOS does, how it behaves, and why it's designed that way. Always current with the implementation.
        </p>
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search the Playbook…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && q.trim()) {
                navigate({ to: "/app/playbook/search", search: { q } as any });
              }
            }}
            className="pl-9 h-11"
          />
        </div>
      </header>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Capabilities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {capabilities.map((p) => (
            <Link
              key={p.slug}
              to="/app/playbook/$"
              params={{ _splat: p.slug }}
              className="block p-3 rounded-md border hover:bg-accent"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{p.title}</div>
                <StatusBadge status={p.frontmatter.status} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Recently Verified</h2>
        <div className="space-y-1">
          {recent.map((p) => (
            <Link
              key={p.slug}
              to="/app/playbook/$"
              params={{ _splat: p.slug }}
              className="flex items-center justify-between p-2 rounded hover:bg-accent text-sm"
            >
              <span>{p.title}</span>
              <span className="text-xs text-muted-foreground">
                {p.frontmatter.last_verified} · Sprint {p.frontmatter.last_verified_sprint ?? "—"}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status || status === "prod-ready") return null;
  const tone =
    status === "planned" ? "bg-muted text-muted-foreground" :
    status === "code-complete" ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" :
    "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${tone}`}>{status}</span>;
}